


































































import Vue from 'vue';
import Component from 'vue-class-component';
import CheckoutElementLayout from './CheckoutElementLayout.vue';
import { ValidationObserver } from 'vee-validate';
import { Prop, Watch } from 'vue-property-decorator';
import { OrderDetailsData, Validatable } from '@/project/app/client-pages/checkout/types';
import { debounce } from 'lodash';
import { CountryCallingCodeViewModel } from '@/types/apiServerContract';

@Component({
    components: { CheckoutElementLayout },
    model: {
        prop: 'orderDetailsData',
        event: 'orderDetailsDataUpdated'
    }
})
export default class CheckoutOrderDetails extends Vue implements Validatable {
    reference: string = '';
    comment: string = '';
    contact: string = '';
    additionalEmail: string = '';
    deliveryPhoneNumber: string = '';
    deliveryPhoneNumberCountryCode: string = '';
    deliveryPhoneNumberCountryObject: CountryCallingCodeViewModel | null = null;

    // Debounce in order to avoid 4 incomplete parent-updates during initialization
    debouncedUpdateParent = debounce(this.updateParent, 50);
    validationError = false;

    @Prop(Boolean)
    isReferenceRequired!: boolean;

    @Prop(Boolean)
    isPhoneNumberRequired!: boolean;

    @Prop({ type: Number, default: 0 })
    phoneNumberMinLength!: number;

    // This is temporarily disabled by EET as they don't support its internal functionality (yet), so leaving it here incase they
    // enable it in the future
    @Prop(Boolean)
    isShippingEmailRequired!: boolean;

    @Prop(Object)
    orderDetailsData!: OrderDetailsData | null;

    @Prop(Array)
    countryCodes!: CountryCallingCodeViewModel[] | null;

    @Prop(String)
    deliveryCountryCode!: string;

    @Watch('orderDetailsData', {
        deep: true
    })
    onValueChange(value: OrderDetailsData | undefined) {
        this.reference = value?.reference ?? '';
        this.contact = value?.orderContact ?? '';
        this.comment = value?.comment ?? '';
        this.additionalEmail = value?.additionalEmailForDeliveryUpdates ?? '';
        this.deliveryPhoneNumber = value?.deliveryPhoneNumber ?? '';
        this.deliveryPhoneNumberCountryCode = value?.deliveryPhoneNumberCountryCode || '';

        // So, as some countries share calling codes (e.g. Great Brittan and Isle of Man both share +44), we find the potential countries with the same calling code and then the country for delivery and then looks and country code instead of calling code.
        const valueCountryPhonePrefixCode = value?.deliveryPhoneNumberCountryCode;
        const deliveryCountryObject: undefined | CountryCallingCodeViewModel = this.countryCodes?.find(c => c.countryCode === this.deliveryCountryCode);
        const phoneCountryCode = valueCountryPhonePrefixCode || (deliveryCountryObject?.countryCallingCode ?? null);
        const potentialPhoneCountries: undefined | CountryCallingCodeViewModel[] = this.countryCodes?.filter(c => c.countryCallingCode === phoneCountryCode);
        const phoneCountry = potentialPhoneCountries?.find(c => c.countryCode === deliveryCountryObject?.countryCode) || potentialPhoneCountries?.find(c => c.countryCallingCode === this.deliveryPhoneNumberCountryCode);
        this.deliveryPhoneNumberCountryObject = this.deliveryPhoneNumberCountryObject || phoneCountry || null;
    }

    @Watch('reference')
    onReferenceChange() {
        this.debouncedUpdateParent();
    }

    @Watch('comment')
    onCommentChange() {
        this.debouncedUpdateParent();
    }

    @Watch('contact')
    onContactChange() {
        this.debouncedUpdateParent();
    }

    @Watch('additionalEmail')
    onAdditionalEmailChange() {
        this.debouncedUpdateParent();
    }

    @Watch('deliveryPhoneNumber')
    onDeliveryPhoneNumberChange() {
        this.debouncedUpdateParent();
    }

    @Watch('deliveryPhoneNumberCountryObject')
    onDeliveryPhoneNumberCountryCodeCodeChange() {
        this.debouncedUpdateParent();
    }

    updateParent() {
        this.validationError = false;
        this.$emit('orderDetailsDataUpdated', {
            additionalEmailForDeliveryUpdates: this.additionalEmail,
            comment: this.comment,
            orderContact: this.contact,
            reference: this.reference,
            deliveryPhoneNumber: this.deliveryPhoneNumber,
            deliveryPhoneNumberCountryCode: this.deliveryPhoneNumberCountryObject?.countryCallingCode || this.deliveryPhoneNumberCountryCode || ''
        } as OrderDetailsData);
    }

    get isPhoneNumberPrefixRequired() {
        return this.isPhoneNumberRequired && !this.deliveryPhoneNumber.startsWith('+');
    }

    $refs!: Vue['$refs'] & {
        validationObserver: InstanceType<typeof ValidationObserver>
    }

    async validate(): Promise<boolean> {
        const valid = await this.$refs.validationObserver.validate();
        this.validationError = !valid;
        return Promise.resolve(valid);
    }
}
